import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

/**
 * Listeners related to the Blocks
 * @param opt The grapesjs config object
 */
export default (opt: PrimaryConfigOptions): void => {
  const editor = opt.editor;

  // eslint-disable-next-line no-restricted-globals, @typescript-eslint/no-unsafe-call
  window.PMW.onClick($('.js-add-block-dialog'), null, function (): void {
    editor.runCommand(window.PMW.emails.commands.OPEN_ADD_BLOCKS_DIALOG);
  });
};

interface AppendBlockOptions {
  event?: Event;
}

/**
 * default grapesJS click handler with following customisations:
 * - if selected: appending blocks next to current selection in parent.
 * - else: appending blocks to end of postermywall-content-container instead of root Wrapper element
 * @param block The block to append
 * @param editor The grapesjs editor instance
 * @param options Options object containing event
 * @param insertAtPosition Position to insert at
 * @returns The created component or undefined
 */
export function appendBlockOnClick(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  block: any,
  editor: Editor,
  {event: _event}: AppendBlockOptions = {},
  insertAtPosition: number | null = null
): Component | undefined {
  // Remove the unused eslint-disable directive
  const content = block.get('content');
  const selected = editor.getSelected();
  let target: Component | undefined;
  let valid: boolean;
  let insertAt: number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const htmlContent = editor.runCommand(window.PMW.emails.commands.GET_INLINED_HTML) as string;

  if (htmlContent.length > window.PMW.emails.MAX_HTML_CONTENT_LENGTH) {
    return;
  }

  const trackingEvent = block.get('trackingEvent') as string | undefined;
  if (trackingEvent) {
    window.PMW.gtm.trackGA4CustomEvent(trackingEvent);
  }

  // If there is a selected component, try first to append
  // the block inside, otherwise, try to place it as a next sibling
  if (selected) {
    valid = editor.Components.canMove(selected, content).result;

    if (valid) {
      target = selected;
    } else {
      let parent = selected.parent();

      if (!parent) {
        const wrapperComponents = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_WRAPPER);
        parent = wrapperComponents?.[0];
      }

      if (parent) {
        valid = editor.Components.canMove(parent, content).result;
      }
      if (valid && parent) {
        target = parent;
        insertAt = parent.components().indexOf(selected) + 1;
      }
    }

    // If no valid target was found and the block to be inserted is a SECTION,
    // traverse up from the selected component to find the parent PMW_CONTENT_CONTAINER and insert after it
    if (!target && block.attributes.content.type === window.PMW.emails.components.table.PMW_SECTION) {
      let current: Component | null = selected;
      let child: Component | null = null;
      let contentContainer: Component | null = null;

      while (current) {
        if (current.attributes.type === window.PMW.emails.components.container.PMW_CONTENT_CONTAINER) {
          contentContainer = current;
          break;
        }
        child = current;
        current = current.parent() ?? null;
      }

      valid = contentContainer ? editor.Components.canMove(contentContainer, content).result : false;

      if (valid && contentContainer && child) {
        target = contentContainer;
        insertAt = target.components().indexOf(child) + 1;
      }
    }
  }

  // If no target found yet, try to append the block to the wrapper
  if (!target) {
    const contentContainers = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_CONTENT_CONTAINER);
    const contentContainer = contentContainers?.[0];
    if (contentContainer) {
      valid = editor.Components.canMove(contentContainer, content).result;
      if (valid) target = contentContainer;
    }
  }

  if (insertAtPosition !== null) {
    insertAt = insertAtPosition;
  }

  const result = target?.append(content, {at: insertAt})?.[0];
  const oldKey = 'activeOnRender';
  const oldActive = content.get?.(oldKey);
  const toActive = block.get('activate') || oldActive;
  const toSelect = block.get('select');

  if (result) {
    if (toSelect || (toActive && toSelect !== false)) {
      // executed after a small timeout to let media-components render for more accurate scrolling
      setTimeout(() => editor.select(result, {scroll: true}), 50);
    }

    if (toActive) {
      result.trigger('active');
      oldActive && result.unset(oldKey);
    }

    if (block.get('resetId')) {
      result.onAll((block: Component) => block.resetId());
    }
  }

  return result;
}